import React from 'react';
import { createPortal } from 'react-dom';
import { useTutorial } from '../../contexts/TutorialContext';
import { getTutorialSections } from '../../tutorials';
import { renderTutorialSectionLabel } from '../../tutorials/formatTutorialSectionLabel';
import type { TutorialDefinition } from '../../tutorials/types';
import { openMainPresentationTab } from '../../utils/openMainPresentationTab';
import {
  TUTORIAL_DESKTOP_ONLY_MESSAGE,
  TUTORIAL_MIN_VIEWPORT_WIDTH,
  isTutorialDesktopViewport,
} from '../../utils/tutorialViewport';
import { PAGE_ZOOM_CHANGED_EVENT } from '../../utils/pageZoom';
import './tutorial.css';

interface TutorialLauncherModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  gradeId?: string | null;
}

const CORE_TUTORIAL_ID = 'core-onboarding';
const MAIN_PRESENTATION_TUTORIAL_ID = 'main-presentation';

/** 런처 카테고리: 교육과정 노트(기존 1·2열) / 기타 노트(Todo·예산·대시보드) */
const LAUNCHER_NAV_CATEGORIES: Array<{ title: string; ids: string[] }> = [
  {
    title: '교육과정 노트',
    ids: [
      'core-onboarding',
      'learning-content',
      'evaluation-plan',
      'class-presentation',
      'class-worksheet',
      'record-memos-weekly-guide',
      'assignment-note',
      'point-coin-item',
      'main-presentation',
      'class-alarm',
    ],
  },
  {
    title: '기타 노트',
    ids: ['todo-note', 'budget-note', 'dashboard-note'],
  },
];

function tutorialStatus(
  def: TutorialDefinition,
  progressMap: ReturnType<typeof useTutorial>['progressMap']
): { label: string; mod: 'done' | 'ing' | 'new'; completed: boolean; inProgress: boolean } {
  const progress = progressMap[def.id];
  const completed = Boolean(progress?.completed);
  const inProgress = !completed && Boolean(progress) && progress.currentStep > 0;
  if (completed) return { label: '완료', mod: 'done', completed, inProgress };
  if (inProgress) return { label: '진행 중', mod: 'ing', completed, inProgress };
  return { label: '시작 전', mod: 'new', completed, inProgress };
}

/** 해당 구간을 지나 다음 구간(또는 튜토리얼 끝)에 도달했으면 완료로 본다. */
function isTutorialSectionDone(
  sectionIndex: number,
  sections: ReturnType<typeof getTutorialSections>,
  progress: ReturnType<typeof useTutorial>['progressMap'][string] | undefined,
  totalSteps: number
): boolean {
  if (!progress) return false;
  if (progress.completed) return true;
  const nextStart = sections[sectionIndex + 1]?.stepIndex ?? totalSteps;
  return progress.currentStep >= nextStart;
}

const TutorialLauncherModal: React.FC<TutorialLauncherModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  gradeId,
}) => {
  const { definitions, progressMap, startTutorial, resetTutorialProgress, exitTutorial, activeTutorial } =
    useTutorial();
  const ordered = React.useMemo(() => {
    const featured = definitions.find((d) => d.id === CORE_TUTORIAL_ID);
    const others = definitions.filter((d) => d.id !== CORE_TUTORIAL_ID);
    return featured ? [featured, ...others] : others;
  }, [definitions]);

  const navCategories = React.useMemo(() => {
    const byId = new Map(ordered.map((d) => [d.id, d]));
    const used = new Set<string>();
    const categories = LAUNCHER_NAV_CATEGORIES.map((cat) => {
      const items: TutorialDefinition[] = [];
      cat.ids.forEach((id) => {
        const def = byId.get(id);
        if (!def || used.has(id)) return;
        used.add(id);
        items.push(def);
      });
      return { title: cat.title, items };
    }).filter((cat) => cat.items.length > 0);
    const leftovers = ordered.filter((def) => !used.has(def.id));
    if (leftovers.length > 0) {
      if (categories[0]) categories[0].items.push(...leftovers);
      else categories.push({ title: '교육과정 노트', items: leftovers });
    }
    return categories;
  }, [ordered]);

  const launchTutorial = React.useCallback(
    (tutorialId: string, opts?: { restart?: boolean; stepIndex?: number }) => {
      if (!isTutorialDesktopViewport()) {
        window.alert(TUTORIAL_DESKTOP_ONLY_MESSAGE);
        return;
      }
      onClose();
      if (tutorialId === MAIN_PRESENTATION_TUTORIAL_ID) {
        const def = definitions.find((d) => d.id === tutorialId);
        const guideStepIdx = def?.steps.findIndex((s) => s.id === 'guide-overview') ?? -1;
        let step = 0;
        if (typeof opts?.stepIndex === 'number') {
          step = opts.stepIndex;
        } else if (!opts?.restart) {
          const existing = progressMap[tutorialId];
          if (existing && !existing.completed) {
            step = Math.max(0, existing.currentStep);
          }
        }
        // 네비바 버튼으로 탭을 연 뒤의 단계부터 이어할 때만 새 탭에서 바로 시작
        if (guideStepIdx >= 0 && step >= guideStepIdx) {
          const opened = openMainPresentationTab({
            workspaceId,
            gradeId: (gradeId ?? '').trim() || undefined,
            tutorialId: MAIN_PRESENTATION_TUTORIAL_ID,
            tutorialStep: step,
          });
          if (!opened) {
            window.alert(
              '팝업이 차단되어 메인 프레젠테이션 탭을 열 수 없어요. 브라우저에서 팝업을 허용한 뒤 다시 시도해 주세요.'
            );
            return;
          }
          if (activeTutorial?.id === MAIN_PRESENTATION_TUTORIAL_ID) {
            exitTutorial();
          }
          return;
        }
        // 시작·네비바 열기 단계는 메인 앱에서 진행
        startTutorial(tutorialId, opts);
        return;
      }
      startTutorial(tutorialId, opts);
    },
    [
      onClose,
      progressMap,
      workspaceId,
      gradeId,
      startTutorial,
      definitions,
      activeTutorial?.id,
      exitTutorial,
    ]
  );

  const defaultSelectedId = React.useMemo(() => {
    const inProgress = ordered.find((d) => {
      const p = progressMap[d.id];
      return p && !p.completed && p.currentStep > 0;
    });
    if (inProgress) return inProgress.id;
    if (ordered.some((d) => d.id === CORE_TUTORIAL_ID)) return CORE_TUTORIAL_ID;
    return ordered[0]?.id ?? null;
  }, [ordered, progressMap]);

  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [desktopOk, setDesktopOk] = React.useState(() => isTutorialDesktopViewport());
  const activeId = selectedId && ordered.some((d) => d.id === selectedId) ? selectedId : defaultSelectedId;
  const selected = ordered.find((d) => d.id === activeId) ?? null;

  React.useEffect(() => {
    if (!isOpen) setSelectedId(null);
  }, [isOpen]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const sync = () => setDesktopOk(isTutorialDesktopViewport());
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, sync as EventListener);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, sync as EventListener);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedStatus = selected ? tutorialStatus(selected, progressMap) : null;
  const sections = selected ? getTutorialSections(selected) : [];
  const selectedProgress = selected ? progressMap[selected.id] : undefined;

  return createPortal(
    <div
      className="cp-tutorial-launcher-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-tutorial-launcher-title"
    >
      <div className="cp-tutorial-launcher-dim" aria-hidden onClick={onClose} />
      <div className="cp-tutorial-launcher-panel" onClick={(e) => e.stopPropagation()}>
        <div className="cp-tutorial-launcher-header">
          <div className="cp-tutorial-launcher-header-text">
            <h2 id="cp-tutorial-launcher-title" className="cp-tutorial-launcher-title">
              튜토리얼
            </h2>
            <p className="cp-tutorial-launcher-desc">
              왼쪽에서 주제를 고른 뒤, 처음부터 시작하거나 원하는 단계부터 바로 진행해 보세요.
            </p>
          </div>
          <button type="button" className="cp-tutorial-launcher-close" onClick={onClose} aria-label="닫기">
            <img src="/modal-close.svg" alt="" width={18} height={18} />
          </button>
        </div>

        <p className="cp-tutorial-launcher-requirements" role="note">
          튜토리얼은 가로 {TUTORIAL_MIN_VIEWPORT_WIDTH}px 이상의 PC 화면에서만 이용할 수 있어요.
        </p>
        {!desktopOk ? (
          <p className="cp-tutorial-launcher-requirements cp-tutorial-launcher-requirements--warn" role="alert">
            {TUTORIAL_DESKTOP_ONLY_MESSAGE}
          </p>
        ) : null}

        <div className="cp-tutorial-launcher-split">
          <nav className="cp-tutorial-launcher-nav" aria-label="튜토리얼 목록">
            <div className="cp-tutorial-launcher-nav-categories">
              {navCategories.map((category) => (
                <section
                  key={category.title}
                  className="cp-tutorial-launcher-nav-category"
                  aria-label={category.title}
                >
                  <h3 className="cp-tutorial-launcher-nav-category-title">
                    {category.title}
                  </h3>
                  <ul className="cp-tutorial-launcher-nav-list">
                    {category.items.map((def) => {
                      const status = tutorialStatus(def, progressMap);
                      const isActive = def.id === activeId;
                      return (
                        <li key={def.id}>
                          <button
                            type="button"
                            className={
                              'cp-tutorial-launcher-nav-item' +
                              (isActive ? ' is-active' : '') +
                              (status.completed ? ' is-completed' : '')
                            }
                            aria-current={isActive ? 'true' : undefined}
                            onClick={() => setSelectedId(def.id)}
                          >
                            <div className="cp-tutorial-launcher-nav-item-top">
                              <span className="cp-tutorial-launcher-nav-item-title">
                                {def.title}
                              </span>
                              <span
                                className={`cp-tutorial-launcher-status cp-tutorial-launcher-status--${status.mod}`}
                              >
                                {status.label}
                              </span>
                            </div>
                            <span className="cp-tutorial-launcher-nav-item-summary">
                              {def.summary}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </nav>

          <section className="cp-tutorial-launcher-detail" aria-live="polite">
            {selected && selectedStatus ? (
              <>
                <div className="cp-tutorial-launcher-detail-head">
                  <div className="cp-tutorial-launcher-detail-head-row">
                    <h3 className="cp-tutorial-launcher-detail-title">{selected.title}</h3>
                    <span
                      className={`cp-tutorial-launcher-status cp-tutorial-launcher-status--${selectedStatus.mod}`}
                    >
                      {selectedStatus.label}
                    </span>
                  </div>
                  <div className="cp-tutorial-launcher-detail-actions">
                    <div className="cp-tutorial-launcher-detail-actions-main">
                      <button
                        type="button"
                        className="cp-tutorial-launcher-card-btn cp-tutorial-launcher-card-btn--primary"
                        disabled={!desktopOk}
                        title={desktopOk ? undefined : TUTORIAL_DESKTOP_ONLY_MESSAGE}
                        onClick={() => launchTutorial(selected.id, { restart: true })}
                      >
                        {selected.id === MAIN_PRESENTATION_TUTORIAL_ID
                          ? '새 탭에서 시작'
                          : '시작하기'}
                      </button>
                      {selectedStatus.inProgress ? (
                        <button
                          type="button"
                          className="cp-tutorial-launcher-card-btn cp-tutorial-launcher-card-btn--ghost"
                          disabled={!desktopOk}
                          title={desktopOk ? undefined : TUTORIAL_DESKTOP_ONLY_MESSAGE}
                          onClick={() => launchTutorial(selected.id, { restart: false })}
                        >
                          {selected.id === MAIN_PRESENTATION_TUTORIAL_ID
                            ? '새 탭에서 이어하기'
                            : '이어하기'}
                        </button>
                      ) : null}
                    </div>
                    {selectedStatus.inProgress || selectedStatus.completed ? (
                      <button
                        type="button"
                        className="cp-tutorial-launcher-card-btn cp-tutorial-launcher-card-btn--reset"
                        onClick={() => {
                          if (
                            !window.confirm(
                              `「${selected.title}」 튜토리얼 진행 상태를 초기화할까요?`
                            )
                          ) {
                            return;
                          }
                          resetTutorialProgress(selected.id);
                        }}
                      >
                        진행 초기화
                      </button>
                    ) : null}
                  </div>
                </div>

                {sections.length > 1 ? (
                  <div className="cp-tutorial-launcher-detail-sections">
                    <h4 className="cp-tutorial-launcher-detail-sections-title">단계별로 바로 시작하기</h4>
                    <ul
                      className={
                        'cp-tutorial-launcher-sections-list' +
                        (sections.length > 8 ? ' is-dense' : '')
                      }
                      style={
                        sections.length > 8
                          ? ({
                              ['--tutorial-section-rows' as string]: Math.ceil(sections.length / 2),
                            } as React.CSSProperties)
                          : undefined
                      }
                    >
                      {sections.map((section, idx) => {
                        const sectionDone = isTutorialSectionDone(
                          idx,
                          sections,
                          selectedProgress,
                          selected.steps.length
                        );
                        return (
                          <li key={`${selected.id}-${section.stepIndex}`}>
                            <button
                              type="button"
                              className={
                                'cp-tutorial-launcher-sections-item' +
                                (sectionDone ? ' is-done' : '')
                              }
                              disabled={!desktopOk}
                              title={desktopOk ? undefined : TUTORIAL_DESKTOP_ONLY_MESSAGE}
                              onClick={() =>
                                launchTutorial(selected.id, { stepIndex: section.stepIndex })
                              }
                            >
                              <span
                                className={
                                  'cp-tutorial-launcher-sections-item-num' +
                                  (sectionDone ? ' is-done' : '')
                                }
                                aria-hidden
                              >
                                {sectionDone ? (
                                  <svg
                                    className="cp-tutorial-launcher-sections-item-check"
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M5 12.5l5 5L19 7"
                                      stroke="currentColor"
                                      strokeWidth="3"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                ) : (
                                  idx + 1
                                )}
                              </span>
                              <span className="cp-tutorial-launcher-sections-item-label">
                                {renderTutorialSectionLabel(section.label)}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : (
                  <p className="cp-tutorial-launcher-detail-empty-sections">
                    이 튜토리얼은 처음부터 순서대로 진행해요.
                  </p>
                )}
              </>
            ) : (
              <p className="cp-tutorial-launcher-detail-empty">표시할 튜토리얼이 없습니다.</p>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TutorialLauncherModal;
